import * as React from "react";
import { HotelRecentReviewsProps } from "./typings";
import { UitkCard, UitkCardContent, UitkCardImageLayout, UitkCardMedia } from "uitk-react-cards";
import { HotelReview } from "typings/microserviceModels/hotel-recent-reviews-flex-module";
import TruncatedText from "shiitake";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import LocalizedReviewTitleAndRating from "components/flexComponents/HotelRecentReviews/components/ReviewTitleAndRating";
import { LocalizedReviewAuthorAndPostedDate } from "components/flexComponents/HotelRecentReviews/components/ReviewAuthorAndPostedDate";
import { LocalizedReviewCardLink } from "components/flexComponents/HotelRecentReviews/components/ReviewLink";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";
import IsomorphicCarousel from "src/components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";

export const HotelRecentReviews = (props: HotelRecentReviewsProps) => {
  const flexComponent = props.flexComponent;
  const model = flexComponent.model;
  const keyHelper = new ItemKeyHelper("hotel-recent-reviews");

  if (!model || !model.hotelReviewList || model.hotelReviewList.length === 0) {
    return null;
  }

  const { title, hotelReviewList } = model;

  return (
    <Viewport>
      <ViewSmall>
        <div
          className="HotelRecentReviews all-t-padding-three all-b-padding-three all-cell-1-1 uitk-cell"
          id={flexComponent.id}
          data-fm={flexComponent.fmId}
          data-fm-title-id={flexComponent.fmTitleId}
        >
          <h2 className="uitk-type-heading-600 all-t-padding-one">{title}</h2>
          {hotelReviewList.map((review, index) => (
            <UitkCard key={keyHelper.next()} className="all-y-margin-three uitk-type-300">
              <ReviewCardContent hotelReview={review} index={index + 1} />
            </UitkCard>
          ))}
        </div>
      </ViewSmall>
      <ViewMedium>
        <div
          className="HotelRecentReviews all-t-padding-three all-b-padding-three all-cell-1-1 uitk-cell"
          id={flexComponent.id}
          data-fm={flexComponent.fmId}
          data-fm-title-id={flexComponent.fmTitleId}
        >
          <div className="carouselMediumContainer">
            <h2 className="uitk-type-heading-600 all-b-padding-two">{model.title}</h2>
            <IsomorphicCarousel
              itemsVisible={{ sm: 1, md: 1, lg: 2 }}
              pageBy={1}
              peekWidth={11}
              buttonText={{
                nextButton: props.l10n.formatText("carousel.item.next"),
                prevButton: props.l10n.formatText("carousel.item.prev")
              }}
            >
              {hotelReviewList.map((hotel, hotelIndex) => (
                <div key={keyHelper.next()}>{HotelRecentReviewCarouselCard(hotel, hotelIndex)}</div>
              ))}
            </IsomorphicCarousel>
          </div>
        </div>
      </ViewMedium>
    </Viewport>
  );
};

const ReviewCardContent = ({ hotelReview, index }: { hotelReview: HotelReview; index: number }) => (
  <UitkCardContent className="all-x-padding-three all-y-padding-three">
    <LocalizedReviewTitleAndRating
      rating={hotelReview.rating}
      reviewTitle={hotelReview.reviewTitle}
    />
    <ReviewText reviewText={hotelReview.reviewText} />
    <LocalizedReviewAuthorAndPostedDate
      authorName={hotelReview.authorName}
      authorLocation={hotelReview.authorLocation}
      datePosted={hotelReview.date}
      hotelName={hotelReview.hotelName}
      index={index}
    />
    <LocalizedReviewCardLink
      index={index}
      hotelName={hotelReview.hotelName}
      hotelInfositeUrl={hotelReview.hotelInfositeUrl}
    />
  </UitkCardContent>
);

const ReviewText = ({ reviewText }: { reviewText: string }) => (
  <Viewport>
    <ViewSmall>
      <div className="uitk-cell all-y-padding-two">
        <TruncatedText renderFullOnServer={true} lines={4}>
          {reviewText}
        </TruncatedText>
      </div>
    </ViewSmall>
    <ViewMedium>
      <div className="uitk-cell all-y-padding-one">
        <TruncatedText className="uitk-type-300" renderFullOnServer={true} lines={2}>
          {reviewText}
        </TruncatedText>
      </div>
    </ViewMedium>
  </Viewport>
);

function HotelRecentReviewCarouselCard(hotelRecentReview: HotelReview, index: number) {
  return (
    <UitkCard
      className="HotelRecentReviewsCard uitk-grid"
      imageLayout={UitkCardImageLayout.LEFT_FULLBLEED}
      withBorder={true}
    >
      <UitkCardMedia className="uitk-cell" pxWidth={120}>
        <UitkFigure ratio={UitkFigureAspectRatioType.R1_1}>
          <LazyLoadedImage
            src={hotelRecentReview.hotelImageUrl}
            alt={hotelRecentReview.hotelName}
          />
        </UitkFigure>
      </UitkCardMedia>
      <ReviewCardContent hotelReview={hotelRecentReview} index={index} />
    </UitkCard>
  );
}

export default HotelRecentReviews;
